import { BASE_API_URL } from "../config/runtime";
import type { BaseResponse } from "./api-contract";
import { authStorage } from "../features/auth/shared/storage/auth-storage";
import { normalizeUrl } from "./axios-helpers";

export const AUTH_SESSION_EXPIRED_EVENT = "auth:session-expired";
export const AUTH_SESSION_RESTORED_EVENT = "auth:session-restored";

interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
}

// Token management utilities
export const authTokenService = {
  // Get current access token
  getAccessToken: (): string | null => {
    return authStorage.load()?.token ?? null;
  },

  // Get current refresh token
  getRefreshToken: (): string | null => {
    return authStorage.load()?.refreshToken ?? null;
  },

  // Set tokens in storage
  setTokens: (access_token: string, refresh_token: string): void => {
    authStorage.updateTokens(access_token, refresh_token);
  },

  // Clear all auth data
  clearAuth: (): void => {
    authStorage.clear();
  },

  resetSessionExpiredHandling: (): void => {
    window.dispatchEvent(new CustomEvent(AUTH_SESSION_RESTORED_EVENT));
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!authTokenService.getAccessToken();
  },

  // Manual token refresh function
  refreshToken: async (): Promise<{ access_token: string; refresh_token: string }> => {
    const refresh_token = authTokenService.getRefreshToken();
    const current_token = authTokenService.getAccessToken();

    if (!refresh_token || !current_token) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await fetch(normalizeUrl(`${BASE_API_URL}/v1/auth/refresh-token`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh_token,
          token: current_token,
        }),
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed with status ${response.status}`);
      }

      const data: BaseResponse<RefreshTokenResponse> = await response.json();

      if (data.success && data.data) {
        const { access_token, refresh_token: new_refresh_token } = data.data;
        authTokenService.setTokens(access_token, new_refresh_token);
        return { access_token, refresh_token: new_refresh_token };
      } else {
        throw new Error(data.user_message || data.message || "Token refresh failed");
      }
    } catch (error: unknown) {
      authTokenService.clearAuth();
      throw error;
    }
  },

  // React Router owns the actual navigation after receiving this event.
  redirectToLogin: (message?: string): void => {
    window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT, { detail: { message } }));
  },
};
